import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatEtTime,
  Input,
  Label,
  minutesToHm,
  SearchableSelect,
  searchableSource,
  SegmentedControl,
} from '@eo/shared';

import type { Department } from './api/types';
import { DEPARTMENT_OPTIONS } from './constants';
import { parseDuration, stepDuration, toQuarterHours } from './helpers';
import { SlackTranscript } from './SlackTranscript';
import type { EditRowDialogProps } from './types';

const NO_CLIENT = '__none__';
const NO_TASK_TYPE = '__none__';

// Two things wear the same dialog because they are the same intent — fix this
// block — but they are not the same object. A proposal is a guess held locally
// until it is confirmed, so anything about it can change. A logged row is a
// ClickUp time entry, and its name and client belong to the task it points at,
// which the whole team shares: renaming that from a timesheet would rename it
// for everyone. So a logged row is offered the entry's own fields instead —
// which task the time counts against, how long, and whether it is variable.
export function EditRowDialog({
  row,
  options,
  taskTypes,
  disabled,
  onQueryOptions,
  onSave,
  onPatchEntry,
  onSetTaskType,
  onClose,
  readOnly,
  date,
  creating = false,
}: EditRowDialogProps) {
  const locked = disabled || readOnly;
  const [title, setTitle] = useState(row.title);
  // Kept here rather than looked up in the results: the results are only the
  // registry's answer to the last search, which may not include it.
  const [chosenClient, setChosenClient] = useState<{ id: string | null; name: string } | null>(
    row.client ? { id: row.clientId ?? null, name: row.client } : null,
  );
  const [variable, setVariable] = useState(row.variable);
  const [durationText, setDurationText] = useState(minutesToHm(row.durationMinutes));
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskTypeId, setTaskTypeId] = useState(row.taskTypeId);
  const [department, setDepartment] = useState<Department>(row.department);

  // A logged entry is a ClickUp time entry pointing at a task. Its name and its
  // client belong to that task, and renaming it here would rename a record the
  // whole team shares — so what is offered instead is which task this time
  // counts against, which is the entry's own field.
  const logged = row.confirmed && row.entryId !== null;
  // Unless there is no task behind it. A hand-made entry keeps its name in its
  // own description, so renaming it touches nothing anyone else can see.
  const ownName = !logged || row.kind === 'manual';
  // A ClickUp task, logged or not, already sits in a list the team shares, and
  // so does any logged entry on one. What has no task yet — a meeting, a Slack
  // block, or time drawn onto the day — can still be filed under a department.
  const canFile = logged ? row.kind === 'manual' && row.department === 'csd' : row.kind !== 'task';
  const internal = department !== 'csd';
  // A retired type still names the time already spent on it, though it is no
  // longer offered.
  const taskTypeName =
    taskTypes.find((t) => t.id === taskTypeId)?.name ??
    options.taskTypes.items.find((t) => t.id === taskTypeId)?.name ??
    (taskTypeId === row.taskTypeId ? (row.taskTypeName ?? undefined) : undefined);
  const chosenTaskName = options.tasks.items.find((t) => t.id === taskId)?.name;
  const clientSearch = searchableSource(options.clients, (q) =>
    onQueryOptions({ source: 'clients', ...q }),
  );
  const taskTypeSearch = searchableSource(options.taskTypes, (q) =>
    onQueryOptions({
      source: 'taskTypes',
      ...q,
      ...(internal ? { filters: { department } } : {}),
    }),
  );
  const chooseDepartment = (next: Department) => {
    if (next === department) return;
    setDepartment(next);
    // A type belongs to one department's list, so it does not travel.
    setTaskTypeId(null);
    if (next !== 'csd') {
      setChosenClient(null);
      setTaskId(null);
      // Internal work is not variable client time; the CFO can still tick it.
      setVariable(false);
    }
  };
  const taskSearch = searchableSource(options.tasks, (q) =>
    onQueryOptions({ source: 'tasks', ...q, filters: { clientId: chosenClient?.id ?? '' } }),
  );

  const totalMinutes = parseDuration(durationText);
  const canSave = totalMinutes !== null && (!ownName || title.trim().length > 0);

  // A duration that cannot be read has no quarter hour to step from.
  const step = (direction: 1 | -1) => {
    if (totalMinutes !== null) setDurationText(minutesToHm(stepDuration(totalMinutes, direction)));
  };

  const save = () => {
    if (totalMinutes === null) return;
    if (logged) {
      onPatchEntry({
        durationMinutes: toQuarterHours(totalMinutes),
        variable,
        ...(taskId === null ? {} : { taskId }),
        ...(ownName ? { note: title.trim() } : {}),
        ...(department !== 'csd' && department !== row.department
          ? { toDepartment: { department, name: title.trim(), date, taskTypeId } }
          : {}),
      });
      if (taskTypeId !== row.taskTypeId) onSetTaskType(taskTypeId);
      onClose();
      return;
    }
    onSave({
      title: title.trim(),
      client: internal ? null : (chosenClient?.name ?? null),
      clientId: internal ? null : (chosenClient?.id ?? null),
      durationMinutes: toQuarterHours(totalMinutes),
      variable,
      taskTypeId,
      department,
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {creating
              ? 'Log this time'
              : readOnly
                ? logged
                  ? 'This logged time'
                  : 'This block'
                : logged
                  ? 'Edit this logged time'
                  : 'Edit this block'}
          </DialogTitle>
          {creating && (
            <p className="text-sm tabular-nums text-text-tertiary">
              {formatEtTime(row.startTs)} · {minutesToHm(row.durationMinutes)}
            </p>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {row.transcript && row.transcript.length > 0 && (
            <SlackTranscript lines={row.transcript} />
          )}

          {ownName && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-title">What did you work on?</Label>
              <Input
                id="edit-title"
                autoFocus={creating}
                value={title}
                disabled={locked}
                onChange={(event) => setTitle(event.target.value)}
                aria-label="Block title"
              />
            </div>
          )}

          {canFile && (
            <div className="flex flex-col gap-1.5">
              <Label>Department</Label>
              <SegmentedControl<Department>
                ariaLabel="Department"
                value={department}
                onChange={chooseDepartment}
                options={DEPARTMENT_OPTIONS.map((option) => ({
                  ...option,
                  disabled: locked,
                }))}
                className="flex-wrap"
              />
            </div>
          )}

          {!internal && (
            <div className="flex flex-col gap-1.5">
              <Label>{logged ? 'Find the task in' : 'Client'}</Label>
              <SearchableSelect
                value={chosenClient?.name ?? NO_CLIENT}
                selectedLabel={chosenClient?.name}
                disabled={locked}
                ariaLabel="Client"
                placeholder="No client"
                options={[
                  { value: NO_CLIENT, label: 'No client', pinned: true },
                  ...options.clients.items.map((c) => ({ value: c.name, label: c.name })),
                ]}
                {...clientSearch}
                onChange={(value) => {
                  const target = options.clients.items.find((c) => c.name === value) ?? null;
                  setChosenClient(target ? { id: target.id, name: target.name } : null);
                  setTaskId(null);
                }}
              />
            </div>
          )}

          {logged && chosenClient?.id && (
            <div className="flex flex-col gap-1.5">
              <Label>Count this time against</Label>
              <SearchableSelect
                value={taskId ?? ''}
                selectedLabel={chosenTaskName}
                disabled={locked}
                ariaLabel="Count this time against"
                placeholder="Leave where it is"
                emptyLabel="No tasks match"
                options={options.tasks.items.map((t) => ({ value: t.id, label: t.name }))}
                {...taskSearch}
                onChange={setTaskId}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Task type</Label>
            <SearchableSelect
              value={taskTypeId ?? NO_TASK_TYPE}
              selectedLabel={taskTypeName}
              disabled={locked}
              ariaLabel="Task type"
              placeholder="No task type"
              options={[
                { value: NO_TASK_TYPE, label: 'No task type', pinned: true },
                ...options.taskTypes.items.map((t) => ({ value: t.id, label: t.name })),
              ]}
              {...taskTypeSearch}
              onChange={(value) => setTaskTypeId(value === NO_TASK_TYPE ? null : value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-duration">How long?</Label>
            <div className="flex items-center gap-1">
              <Input
                id="edit-duration"
                value={durationText}
                disabled={locked}
                onChange={(event) => setDurationText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                  event.preventDefault();
                  step(event.key === 'ArrowUp' ? 1 : -1);
                }}
                // Settles on the grid's own spelling once the field is left, so
                // "90" reads back as the "1h 30m" it was taken to mean.
                onBlur={() => {
                  if (totalMinutes !== null)
                    setDurationText(minutesToHm(toQuarterHours(totalMinutes)));
                }}
                aria-label="Duration"
                aria-invalid={totalMinutes === null}
                aria-describedby={totalMinutes === null ? 'edit-duration-hint' : undefined}
                className="w-32 tabular-nums"
              />
              {/* Stacked like a number field's own spinner, so the arrows read
                  as stepping this value rather than as actions of their own. */}
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={locked || totalMinutes === null}
                  onClick={() => step(1)}
                  aria-label="Increase duration"
                  className="h-4"
                >
                  <ChevronUp className="size-3.5 text-text-secondary" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={locked || totalMinutes === null}
                  onClick={() => step(-1)}
                  aria-label="Decrease duration"
                  className="h-4"
                >
                  <ChevronDown className="size-3.5 text-text-secondary" />
                </Button>
              </div>
            </div>
            {totalMinutes === null && (
              <p id="edit-duration-hint" className="text-xs text-destructive">
                Try 1h 30m, 1:30 or 90
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2 rounded-8 border border-border-faint p-3">
            <input
              type="checkbox"
              checked={variable}
              disabled={locked}
              onChange={(event) => setVariable(event.target.checked)}
              aria-label="Variable"
              className="mt-0.5 size-4 accent-primary"
            />
            <span className="text-sm text-text-primary">
              Variable
              <span className="block text-xs text-text-tertiary">
                Bills against variable hours rather than the fixed retainer.
              </span>
            </span>
          </label>
        </div>

        {!readOnly && (
          <DialogFooter>
            <Button variant="ghost" disabled={disabled} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={disabled || !canSave} onClick={save}>
              {creating ? 'Log time' : 'Save'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
